import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// Pagination
//
// Page navigation with page numbers, prev/next,
// and page size selector.
// Design: §6.9 Pagination component
// ─────────────────────────────────────────────

interface PaginationProps {
  page: number;
  totalPages: number;
  totalItems?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  className?: string;
}

function getPageNumbers(page: number, totalPages: number): (number | "...")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | "...")[] = [];

  if (page <= 4) {
    pages.push(1, 2, 3, 4, 5, "...", totalPages);
  } else if (page >= totalPages - 3) {
    pages.push(
      1,
      "...",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages
    );
  } else {
    pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
  }

  return pages;
}

export function Pagination({
  page,
  totalPages,
  totalItems,
  pageSize = 20,
  pageSizeOptions = [10, 20, 50, 100],
  onPageChange,
  onPageSizeChange,
  className,
}: PaginationProps) {
  const { t, language } = useTranslation();
  // S-162/L-013: defensively clamp against invalid/out-of-range inputs
  // (NaN, 0, negative, or page > totalPages) so the control can never emit a
  // broken page delta or highlight a non-existent page.
  const safeTotal = Math.max(1, Math.trunc(totalPages) || 1);
  const safePage = Math.min(Math.max(1, Math.trunc(page) || 1), safeTotal);
  const pages = getPageNumbers(safePage, safeTotal);

  return (
    <nav
      aria-label={t("pagination.navLabel")}
      className={cn(
        "flex items-center justify-between gap-4 text-caption text-text-muted",
        className
      )}
    >
      {/* Left: row count */}
      <div className="flex items-center gap-2">
        {totalItems != null && (
          <span>
            {formatNumber(totalItems, language)}{" "}
            {t("pagination.results", { count: totalItems })}
          </span>
        )}
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <span>{t("pagination.rows")}:</span>
            <select
              aria-label={t("pagination.rowsPerPage")}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-control border border-border-default bg-bg-input px-2 py-0.5 text-caption text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Right: pagination controls */}
      <div className="flex items-center gap-1">
        {/* Previous */}
        <button
          type="button"
          aria-label={t("pagination.previousPage")}
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          className={cn(
            "flex h-7 items-center gap-1 rounded-control px-2 text-caption transition-colors",
            safePage <= 1
              ? "cursor-not-allowed text-text-muted opacity-40"
              : "hover:bg-hover text-text-secondary"
          )}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          <span>{t("common.previous")}</span>
        </button>

        {/* Page numbers */}
        {pages.map((p, idx) =>
          p === "..." ? (
            <span key={`ellipsis-${idx}`} aria-hidden="true" className="px-1 text-text-muted">
              ...
            </span>
          ) : (
            <button
              key={p}
              type="button"
              aria-label={`${t("pagination.goToPage")} ${p}`}
              aria-current={p === safePage ? "page" : undefined}
              onClick={() => onPageChange(p as number)}
              className={cn(
                "flex h-7 min-w-[28px] items-center justify-center rounded-control px-2 text-caption transition-colors",
                p === safePage
                  ? "bg-accent-bg font-medium text-accent-text"
                  : "hover:bg-hover text-text-secondary"
              )}
            >
              {p}
            </button>
          )
        )}

        {/* Next */}
        <button
          type="button"
          aria-label={t("pagination.nextPage")}
          disabled={safePage >= safeTotal}
          onClick={() => onPageChange(safePage + 1)}
          className={cn(
            "flex h-7 items-center gap-1 rounded-control px-2 text-caption transition-colors",
            safePage >= safeTotal
              ? "cursor-not-allowed text-text-muted opacity-40"
              : "hover:bg-hover text-text-secondary"
          )}
        >
          <span>{t("common.next")}</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </nav>
  );
}
